import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSpring, animated, easings } from '@react-spring/web';
import { ReactSVG } from 'react-svg';
import { Textfit } from 'react-textfit';
import triangle_star from '../../assets/triangle_star.svg';
import dash_star from '../../assets/dash_star.svg';
import multi_color_star_1 from '../../assets/multi_color_star_1.svg';
import multi_color_star_2 from '../../assets/multi_color_star_2.svg';
import multi_color_star_3 from '../../assets/multi_color_star_3.svg';
import facebook from '../../assets/facebook.svg';
import twitter from '../../assets/twitter.svg';
import instagram from '../../assets/instagram.svg';
import sunRays from '../../assets/backgrounds/high_sun_rays.svg';
import '../Slides.scss';
import './OutroPosterSlide.scss';

const OutroSlide = ({ data, triggerClose }) => {
  const { kid_name, poster, poster_share_url } = data;

  const [coverVisible, setCoverVisible] = useState(true);
  const [sharingMode, setSharingMode] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [fadeInDone, setFadeInDone] = useState(false);
  const [pageVisible, setPageVisible] = useState(false);

  const coverSpring = useSpring({
    opacity: coverVisible ? 1 : 0,
    pointerEvents: coverVisible ? 'auto' : 'none',
    config: { duration: 600 },
  });

  const coverSubTitleSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    delay: 600,
  });

  const coverTitleSpring = useSpring({
    from: { opacity: 0, transform: 'translateX(100px)' },
    to: { opacity: 1, transform: 'translateX(0px)' },
    delay: 400,
    config: {
      tension: 180,  // lower tension = more wobble
      friction: 12,  // lower friction = 'less damping'
    },
  });

  const posterSpring = useSpring({
    from: { opacity: 0, transform: 'scale(0.5) translateY(50px)', pointerEvents: 'none' },
    to: pageVisible
      ? { opacity: 1, transform: 'scale(1) translateY(0px)', pointerEvents: 'auto' }
      : { opacity: 0, transform: 'scale(0.5) translateY(50px)', pointerEvents: 'none' },
    config: { tension: 300, friction: 12 },
    delay: 200
  });

  const boxSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(100vh)' },
    to: pageVisible 
     ? { opacity: 1, transform: 'translateY(0px)' }
     : { opacity: 0, transform: 'translateY(100vh)' },
    delay: 0,
  });

  const shareBoxSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(100vh)' },
    to: sharingMode
      ? { opacity: 1, transform: 'translateY(0px)' }
      : { opacity: 0, transform: 'translateY(100vh)' },
    config: { tension: 170, friction: 26 },
  });

  const sunRaysFadeInSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { duration: 1000 },
    delay: 1000,
    onRest: () => setFadeInDone(true),
  });

  const sunRaysScaleSpring = useSpring({
    from: { scale: 1, rotate: '180deg' },
    to: { scale: 1.1 },
    loop: fadeInDone ? { reverse: true } : false,
    config: { duration: 1500, easing: easings.easeInOutSine },
  });

  const sunRaysSpring = {
    ...sunRaysFadeInSpring,
    ...sunRaysScaleSpring,
  };

  // Sparkles
  const appear = useSpring({ from: { opacity: 0 }, to: { opacity: 1 } });
  const spinSparkle = useSpring({
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
    loop: true,
    config: { duration: 3000 },
  });

  const pulseSparkle = useSpring({
    from: { transform: 'scale(1)' },
    to: { transform: 'scale(1.3)' },
    loop: { reverse: true },
  });

  const twinkleSparkle = useSpring({
    from: { opacity: 0.5 },
    to: { opacity: 1 },
    loop: { reverse: true },
    config: { duration: 800 },
  });

  const dashStarSpring = {
    ...appear,
    ...spinSparkle,
    ...twinkleSparkle,
    delay: 1400,
  };

  const triangleStar1Spring = useSpring({ ...appear, ...pulseSparkle, delay: 1500 });
  const triangleStar2Spring = useSpring({ ...appear, delay: 1600 });
  const triangleStar3Spring = useSpring({ ...appear, ...pulseSparkle, delay: 1700 });

  const multiColorStar1Spring = useSpring({
    from: { opacity: 0, transform: 'scale(1) rotate(0deg)' },
    to: [
      { opacity: 1 },
      {
        transform: 'scale(1.1) rotate(8deg)',
        opacity: 0.9,
        loop: { reverse: true },
      },
    ],
    config: { duration: 1600 },
    delay: 1800,
  });

  const fadeIn = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { duration: 600 },
    delay: 1700,
  });

  const spin = useSpring({
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
    loop: true,
    config: { duration: 7000, easing: easings.easeInOutSine },
  });

  const multiColorStar2Spring = { ...fadeIn, ...spin };

  const multiColorStar3Spring = useSpring({
    from: { opacity: 0 },
    to: [{ opacity: 1 }, { opacity: 0.75 }],
    loop: { reverse: true },
    config: { duration: 900 },
    delay: 1900,
  });

  useEffect(() => {
    if (poster) {
      const timer = setTimeout(() => {
        setCoverVisible(false);
        setTimeout(() => {
          setPageVisible(true);
        }, 600); // match coverSpring duration
      }, 4000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [poster]);



  const triggerDownload = () => {
    
    const downloadPage = `
      <!DOCTYPE html>
      <html>
        <head><title>Rocksteady Round-Up! for: ${kid_name}</title></head>
        <body>
          <img src="${poster}" alt="poster" style="max-width: 100%; display: block; margin-bottom: 20px;" />
          <script>
            const link = document.createElement('a');
            link.href = "${poster}";
            link.download = "${poster}";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          </script>
        </body>
      </html>
    `;

    const newWindow = window.open();
    if (!newWindow) {
      alert('Please allow popups to download the image.');
      return;
    }
    newWindow.document.write(downloadPage);
    newWindow.document.close();
  };

  const openTwitterShare = (message, isMobile) => {
    const url = encodeURIComponent(poster_share_url);
    const text = encodeURIComponent(message);
  
    const webUrl = `https://twitter.com/intent/tweet?url=${url}&text=${text}`;
    const appUrl = `twitter://post?message=${text}%20${url}`;
  
    if (isMobile) {
      window.location = appUrl;
      setTimeout(() => {
        window.open(webUrl, '_blank');
      }, 1500);
    } else {
      window.open(webUrl, '_blank', 'width=600,height=400');
    }
  };

  const triggerShare = () => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const message = `Proud parent moment! Here's everything ${kid_name} has achieved with Rocksteady so far this term! ☀️🎸🎤🥁 🎹 ☀️ #RocksteadySummerRoundup25 #MusicInSchools #MusicMatters #BandLessons #MusicEducation`;
    const platforms = ['facebook', 'instagram', 'x'];
    const platform = platforms[activeTab];

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'roundup-share',
      platform,
    });
    
    if (activeTab === 0) {
      navigator.clipboard.writeText(message).then(() => {
        if (window.confirm(`Tap OK to view your shareable Round-up, then save the image.\n\nOpen Facebook and create a new post using your Round-up summary.\n\nWe’ve even written a caption for you—it’s already copied to your clipboard. Just paste it into your post and you’re all set!`)) {
          triggerDownload();
        }
      }).catch(() => {
        alert(`Please copy the caption manually:\n\n"${message}"\n\nThen open Facebook and create a new post. After that, the image will download.`);
        triggerDownload();
      });
      return;

    }

    if (activeTab === 1) {
      navigator.clipboard.writeText(message).then(() => {
        if (window.confirm(`Tap OK to view your shareable Round-up, then save the image.\n\nOpen Instagram and create a new post using your Round-up summary.\n\nWe’ve even written a caption for you—it’s already copied to your clipboard. Just paste it into your post and you’re all set!`)) {
          triggerDownload();
        }
      }).catch(() => {
        alert(`Please copy the caption manually:\n\n"${message}"\n\nThen open Instagram and create a new post. After that, the image will download.`);
        triggerDownload();
      });
      return;
    }

    if (activeTab === 2) {
      openTwitterShare(message, isMobile)
    }
  };


  const sharingBox = () => {
    const tabIcons = [
      <ReactSVG title="Meta" src={facebook} />,
      <ReactSVG title="Instagram" src={instagram} />,
      <ReactSVG title="X" src={twitter} />,
    ];

    const shareTracking = [
      'roundup-share-facebook',
      'roundup-share-instagram', 
      'roundup-share-x'
    ];

    return (
      <animated.div className="Slide__outro__page__sharingBox" style={shareBoxSpring}>
        <div className={`Slide__outro__page__sharingBox__tabs active-tab-${activeTab + 1}`}>
          {tabIcons.map((icon, index) => (
            <button
              key={icon.props.title}
              id={shareTracking[index]}
              type="button"
              className={`Slide__outro__page__sharingBox__tab ${activeTab === index ? 'active' : ''}`}
              onClick={() => setActiveTab(index)}
            >
              {icon}
              <span>{icon.props.title}</span>
            </button>
          ))}
        </div>
        <div className="Slide__outro__page__sharingBox__content">
          <button className="Slide__outro__page__sharingBox__content__shareButton" type="button" onClick={triggerShare}>
            share
          </button>
          <button
            className="Slide__outro__page__sharingBox__content__downloadButton"
            type="button"
            id="roundup-download"
            onClick={triggerDownload}
          >
            download
          </button>
        </div>
      </animated.div>
    );
  };

  const renderCover = () => (
    <animated.div className="Slide__outro__cover" style={coverSpring}>
      {
        poster ? (
          <>
            <animated.h1 className="Slide__outro__cover__subTitle" style={coverTitleSpring}>
              Such amazing progress!
            </animated.h1>
            <animated.h1 className="Slide__outro__cover__title" style={coverSubTitleSpring}>
              Ready to share
              <div>
                <Textfit mode="single" max={47} style={{ maxWidth: '270px' }}>
                  {`${kid_name}’s`}
                </Textfit>
              </div>
              Round-Up?
            </animated.h1>
          </>

        ) : (
          <>
            <animated.h1 className="Slide__outro__cover__title__fallback" style={coverTitleSpring}>
              Amazing Work,
              <Textfit mode="single" max={47} style={{ maxWidth: '270px' }}>
                {`${kid_name}!`}
              </Textfit>
            </animated.h1>
            <animated.h1 className="Slide__outro__cover__subTitle" style={coverSubTitleSpring}>
              We can’t wait to see what’s next
            </animated.h1>
          </>
        )
      }
      {!poster && (
        <button type="button" className="Slide__outro__cover__button" onClick={triggerClose}>
          Go backstage
        </button>
      )}

      <animated.div className="Slide__outro__cover__star__triangle1" style={triangleStar1Spring}>
        <ReactSVG src={triangle_star} />
      </animated.div>
      <animated.div className="Slide__outro__cover__star__triangle2" style={triangleStar2Spring}>
        <ReactSVG src={triangle_star} />
      </animated.div>
      <animated.div className="Slide__outro__cover__star__triangle3" style={triangleStar3Spring}>
        <ReactSVG src={triangle_star} />
      </animated.div>
      <animated.div className="Slide__outro__cover__star__dash" style={dashStarSpring}>
        <ReactSVG src={dash_star} />
      </animated.div>
      <animated.div className="Slide__outro__cover__star__dash2" style={dashStarSpring}>
        <ReactSVG src={dash_star} />
      </animated.div>
      <animated.div className="Slide__outro__cover__star__multiColor1" style={multiColorStar1Spring}>
        <ReactSVG src={multi_color_star_1} />
      </animated.div>
      <animated.div className="Slide__outro__cover__star__multiColor2" style={multiColorStar2Spring}>
        <ReactSVG src={multi_color_star_2} />
      </animated.div>
      <animated.div className="Slide__outro__cover__star__multiColor4" style={multiColorStar1Spring}>
        <ReactSVG src={multi_color_star_2} />
      </animated.div>
      <animated.div className="Slide__outro__cover__star__multiColor3" style={multiColorStar3Spring}>
        <ReactSVG src={multi_color_star_3} />
      </animated.div>
      <animated.div className="Slide__outro__cover__star__multiColor5" style={multiColorStar3Spring}>
        <ReactSVG src={multi_color_star_3} />
      </animated.div>
    </animated.div>
  );

  const renderPage = () => {
    const mode1Content = (
      <p className="Slide__outro__page__box__content">
        Celebrate
        <span>{kid_name}</span>
        ’s awesome Rocksteady Round-up! Share this summary on social media or download and print a copy for them to proudly show off!
      </p>
    );

    const mode2Content = (
      <p className="Slide__outro__page__box__content">
        Select your preferred social media platform and hit “SHARE” to post
        <span>{kid_name}</span>
        ’s amazing Rocksteady accomplishments!
      </p>
    );

    return (
      <div className="Slide__outro__page">
        <animated.img src={poster} alt="poster" className="Slide__outro__page__poster" style={posterSpring} />
        <animated.div
          className="Slide__outro__page__box"
          style={{ ...boxSpring, height: sharingMode ? '67vh' : '55vh' }}
        >
          {sharingMode ? (
            <>
              {mode2Content}
              {sharingBox()}
            </>
          ) : (
            <>
              {mode1Content}
              <button
                className="Slide__outro__page__box__shareButton"
                type="button"
                onClick={() => setSharingMode(true)}
              >
                share / Download
              </button>
            </>
          )}
        </animated.div>
        <animated.div className="Slide__outro__page__sunRays" style={sunRaysSpring}>
          <ReactSVG src={sunRays} />
        </animated.div>
      </div>
    );
  };

  return (
    <div className="Slide__outro">
      {renderCover()}
      {pageVisible && renderPage()}
    </div>
  );
};

OutroSlide.propTypes = {
  data: PropTypes.shape({
    kid_name: PropTypes.string.isRequired,
    poster: PropTypes.string,
    poster_share_url: PropTypes.string
  }).isRequired,
  triggerClose: PropTypes.func.isRequired,
};

export default OutroSlide;
